import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { X } from "lucide-react";
import airtableUtils from "../../../../../services/airtableUtils";
import type { TSession } from "../../../../../utils/types";
import type { ConsultPatient } from "../hooks/useConsultSearch";
import {
  createPatientAndStartQuickRecord,
  QuickRecordNewPatientInput,
  startQuickRecordForExistingPatient,
} from "./quickRecordService";
import { QuickRecordCreatePatientForm } from "./QuickRecordCreatePatientForm";
import { QuickRecordPatientSearch } from "./QuickRecordPatientSearch";

type QuickRecordModalProps = {
  visible: boolean;
  session: TSession | null;
  appointmentDate?: string | null;
  onClose: () => void;
  onReady: (patient: ConsultPatient) => void;
};

type QuickRecordMode = "search" | "create";
type QuickRecordTimeMode = "now" | "custom";

const EMPTY_NEW_PATIENT: QuickRecordNewPatientInput = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: "",
  sex: "",
};

const SEARCH_DEBOUNCE_MS = 350;

const parseAppointmentDate = (value?: string | null): Date => {
  if (!value) {
    return new Date();
  }

  const isoDateMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return new Date(
      Number(isoDateMatch[1]),
      Number(isoDateMatch[2]) - 1,
      Number(isoDateMatch[3]),
    );
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const formatDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildAppointmentDate = (baseDate: Date, time?: string): Date | null => {
  const date = new Date(baseDate);
  const normalized = time?.trim();

  if (!normalized) {
    const now = new Date();
    date.setHours(now.getHours(), now.getMinutes(), 0, 0);
    return date;
  }

  const twelveHourMatch = normalized.match(/^(\d{1,2}):([0-5]\d)\s*(AM|PM)$/i);
  if (twelveHourMatch) {
    const hourRaw = Number(twelveHourMatch[1]);
    const minute = Number(twelveHourMatch[2]);
    const meridiem = twelveHourMatch[3].toUpperCase();
    if (hourRaw < 1 || hourRaw > 12) return null;

    let hour = hourRaw % 12;
    if (meridiem === "PM") hour += 12;

    date.setHours(hour, minute, 0, 0);
    return date;
  }

  const twentyFourHourMatch = normalized.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (twentyFourHourMatch) {
    const hour = Number(twentyFourHourMatch[1]);
    const minute = Number(twentyFourHourMatch[2]);
    date.setHours(hour, minute, 0, 0);
    return date;
  }

  return null;
};

const QuickRecordModal: React.FC<QuickRecordModalProps> = ({
  visible,
  session,
  appointmentDate,
  onClose,
  onReady,
}) => {
  const [mode, setMode] = useState<QuickRecordMode>("search");
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<ConsultPatient[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<ConsultPatient | null>(
    null,
  );
  const [newPatientInput, setNewPatientInput] =
    useState<QuickRecordNewPatientInput>(EMPTY_NEW_PATIENT);
  const [timeMode, setTimeMode] = useState<QuickRecordTimeMode>("now");
  const [customTimeInput, setCustomTimeInput] = useState("");
  const [appointmentDateInput, setAppointmentDateInput] = useState(() =>
    formatDateInputValue(parseAppointmentDate(appointmentDate)),
  );
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPatientId = useMemo(
    () =>
      selectedPatient
        ? String(
            selectedPatient.patientRecordId ||
              selectedPatient.clinicOSUUID ||
              selectedPatient.id,
          )
        : null,
    [selectedPatient],
  );

  const initialAppointmentDateInput = useMemo(
    () => formatDateInputValue(parseAppointmentDate(appointmentDate)),
    [appointmentDate],
  );

  const baseAppointmentDate = useMemo(
    () => parseAppointmentDate(appointmentDateInput),
    [appointmentDateInput],
  );

  const appointmentDateLabel = useMemo(
    () =>
      baseAppointmentDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    [baseAppointmentDate],
  );

  useEffect(() => {
    if (!visible) {
      setMode("search");
      setSearchValue("");
      setSearchResults([]);
      setSelectedPatient(null);
      setSubmitting(false);
      setError(null);
      setNewPatientInput(EMPTY_NEW_PATIENT);
      setShowOptionalFields(false);
      setTimeMode("now");
      setCustomTimeInput("");
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setAppointmentDateInput(initialAppointmentDateInput);
    }
  }, [initialAppointmentDateInput, visible]);

  useEffect(() => {
    if (!visible || mode !== "search") {
      return;
    }

    const query = searchValue.trim();
    if (!query || query.length === 1) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    if (!session?.clinicId) {
      setSearchResults([]);
      setSearchLoading(false);
      setError("Missing clinic session. Please refresh and try again.");
      return;
    }

    setError(null);
    const timeout = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const clinicId = session.clinicId;
        if (!clinicId) {
          setError("Missing clinic session. Please refresh and try again.");
          setSearchResults([]);
          return;
        }
        const results = await airtableUtils.searchPatientsClinicOS(
          query,
          clinicId,
          session,
        );
        setSearchResults(results);
      } catch (searchError) {
        console.error("Quick record patient search failed:", searchError);
        setError("Unable to search patients right now.");
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchValue, visible, mode, session]);

  const handleStartForExistingPatient = async () => {
    if (!selectedPatient || !session) {
      setError("Please select a patient first.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (!appointmentDateInput) {
        setError("Choose an appointment date before starting.");
        setSubmitting(false);
        return;
      }

      const startAt = buildAppointmentDate(
        baseAppointmentDate,
        timeMode === "custom" ? customTimeInput : undefined,
      );
      if (!startAt) {
        setError(
          "Enter a valid custom time (e.g., 3:30 PM or 15:30), or use right now.",
        );
        setSubmitting(false);
        return;
      }

      const preparedPatient = await startQuickRecordForExistingPatient(
        session,
        selectedPatient,
        { startAt },
      );
      onReady(preparedPatient);
      onClose();
    } catch (submitError) {
      console.error("Quick record failed for existing patient:", submitError);
      setError("Unable to start quick record for this patient.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePatientAndStart = async () => {
    if (!session) {
      setError("Missing session. Please refresh and try again.");
      return;
    }

    if (!newPatientInput.firstName.trim() || !newPatientInput.lastName.trim()) {
      setError("First name and last name are required.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (!appointmentDateInput) {
        setError("Choose an appointment date before starting.");
        setSubmitting(false);
        return;
      }

      const startAt = buildAppointmentDate(
        baseAppointmentDate,
        timeMode === "custom" ? customTimeInput : undefined,
      );
      if (!startAt) {
        setError(
          "Enter a valid custom time (e.g., 3:30 PM or 15:30), or use right now.",
        );
        setSubmitting(false);
        return;
      }

      const preparedPatient = await createPatientAndStartQuickRecord(
        session,
        newPatientInput,
        { startAt },
      );
      onReady(preparedPatient);
      onClose();
    } catch (submitError) {
      console.error("Quick record failed for new patient:", submitError);
      setError("Unable to create patient and start quick record.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalCard}>
          <View style={styles.header}>
            <Text style={styles.title}>Quick Record</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <X size={20} color="#374151" />
            </TouchableOpacity>
          </View>

          {mode === "search" ? (
            <QuickRecordPatientSearch
              value={searchValue}
              loading={searchLoading}
              results={searchResults}
              selectedPatientId={selectedPatientId}
              onChangeValue={(value) => {
                setSearchValue(value);
                setSelectedPatient(null);
              }}
              onSelectPatient={(patient) => {
                setSelectedPatient(patient);
                setError(null);
              }}
              onCreateNewPatient={() => {
                setMode("create");
                setError(null);
              }}
            />
          ) : (
            <QuickRecordCreatePatientForm
              value={newPatientInput}
              showOptionalFields={showOptionalFields}
              onChange={setNewPatientInput}
              onToggleOptionalFields={() =>
                setShowOptionalFields((current) => !current)
              }
              onBackToSearch={() => {
                setMode("search");
                setError(null);
              }}
            />
          )}

          {error && <Text style={styles.errorText}>{error}</Text>}

          <View style={styles.timeBox}>
            <Text style={styles.timeTitle}>Appointment time</Text>
            <View style={styles.customTimeInputWrap}>
              <Text style={styles.customTimeInputLabel}>Appointment date</Text>
              <input
                type="date"
                aria-label="Appointment date"
                value={appointmentDateInput}
                onChange={(event) =>
                  setAppointmentDateInput(event.currentTarget.value)
                }
                style={{
                  height: 40,
                  border: "1px solid #cfd8e3",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 14,
                  color: "#0f172a",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  minWidth: 170,
                }}
              />
            </View>
            <View style={styles.timeModeRow}>
              <TouchableOpacity
                style={[
                  styles.timeModeButton,
                  timeMode === "now" && styles.timeModeButtonActive,
                ]}
                onPress={() => setTimeMode("now")}
              >
                <Text
                  style={[
                    styles.timeModeButtonText,
                    timeMode === "now" && styles.timeModeButtonTextActive,
                  ]}
                >
                  Use current time (default)
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.timeModeButton,
                  timeMode === "custom" && styles.timeModeButtonActive,
                ]}
                onPress={() => setTimeMode("custom")}
              >
                <Text
                  style={[
                    styles.timeModeButtonText,
                    timeMode === "custom" && styles.timeModeButtonTextActive,
                  ]}
                >
                  Set custom time
                </Text>
              </TouchableOpacity>
            </View>
            {timeMode === "custom" && (
              <View style={styles.customTimeInputWrap}>
                <Text style={styles.customTimeInputLabel}>
                  Time for {appointmentDateLabel} (e.g., 3:30 PM or 15:30)
                </Text>
                <TextInput
                  style={styles.customTimeInput}
                  value={customTimeInput}
                  onChangeText={setCustomTimeInput}
                  placeholder="3:30 PM"
                  autoCapitalize="none"
                />
              </View>
            )}
          </View>

          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={onClose}
              disabled={submitting}
            >
              <Text style={styles.secondaryButtonText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.primaryButton,
                submitting && styles.primaryButtonDisabled,
              ]}
              onPress={
                mode === "search"
                  ? handleStartForExistingPatient
                  : handleCreatePatientAndStart
              }
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={styles.primaryButtonText}>Start recording</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(14, 23, 38, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalCard: {
    width: "100%",
    maxWidth: 720,
    borderRadius: 14,
    backgroundColor: "#f4f7fb",
    padding: 20,
    gap: 14,
    borderWidth: 1,
    borderColor: "#dce3eb",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    color: "#0f172a",
  },
  closeButton: {
    padding: 6,
  },
  errorText: {
    color: "#c62828",
    fontSize: 13,
  },
  timeBox: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#dce3eb",
    borderRadius: 10,
    padding: 12,
    gap: 8,
  },
  timeTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#0f172a",
  },
  timeHint: {
    fontSize: 13,
    color: "#475569",
  },
  timeModeRow: {
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
  },
  timeModeButton: {
    borderWidth: 1,
    borderColor: "#cfd8e3",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  timeModeButtonActive: {
    borderColor: "#0077e6",
    backgroundColor: "#e8f3ff",
  },
  timeModeButtonText: {
    color: "#334155",
    fontSize: 13,
    fontWeight: "600",
  },
  timeModeButtonTextActive: {
    color: "#0b63b8",
  },
  customTimeInputWrap: {
    gap: 6,
  },
  customTimeInputLabel: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: "500",
  },
  customTimeInput: {
    height: 40,
    borderWidth: 1,
    borderColor: "#cfd8e3",
    borderRadius: 8,
    padding: 10,
    fontSize: 14,
    color: "#0f172a",
    backgroundColor: "#ffffff",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  secondaryButton: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#cfd8e3",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  secondaryButtonText: {
    color: "#334155",
    fontWeight: "600",
  },
  primaryButton: {
    backgroundColor: "#0077e6",
    borderRadius: 8,
    paddingHorizontal: 18,
    paddingVertical: 10,
    minWidth: 132,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonDisabled: {
    opacity: 0.8,
  },
  primaryButtonText: {
    color: "#ffffff",
    fontWeight: "700",
  },
});

export default QuickRecordModal;
